import { Component, OnInit } from '@angular/core';
import { DisplayOptions } from '../../../shared/interfaces/display-options';
import { ReusableFunctions } from '../../../shared/classes/reusable-functions';
import { KPIService } from '../../../shared/services/kpi.service';
import { LimeGearCharts } from '../../../shared/classes/chart-data';
import { KPIData, KPIBand, KPI, IKPIDataParams } from '../../../shared/interfaces/kpi-data';
import { Router } from '@angular/router';
import { ToastService } from '../../../shared/services/toast.service';
import * as Highcharts from 'highcharts';
import {
    NoDataTypes,
    NoDataActionType,
    NoDataSourceType,
} from '../../../shared/interfaces/no-data';
import { DecimalPipe } from '@angular/common';
import { CurrentUserStateService } from '../../../shared/services/current-user-state.service';
import { ImpersonationService } from '../../../shared/services/impersonation.service';
import { ILargeCardInteriorOptions } from 'app/modules/shared/interfaces/card-data';
import { IPositionInfo, PositionService } from '../../../shared/services/position.service';

@Component({
    selector: 'app-performance-insights-kpi',
    templateUrl: './performance-insights-kpi.component.html',
    styleUrls: ['./performance-insights-kpi.component.scss'],
})
export class PerformanceInsightsKPIComponent implements OnInit {
    largeCardOptions: ILargeCardInteriorOptions;
    action: NoDataActionType;
    pageTitle: string = 'KPI Insights';
    records: KPIData[];
    seriesData: any[];
    KPIs: KPI[];
    selectedKPI: KPI[];
    topEntities: KPIData[];
    bottomEntities: KPIData[];
    plotBandsHighChart: KPIBand[];
    targetUnits: string;
    title: string;
    displayOptions: DisplayOptions[];
    hFilterJSON: string;
    limeGearCharts: LimeGearCharts.LimeGearChart;
    switchLabel: string;
    showNames: boolean;
    shouldIgnoreNumberLOs: boolean = false;
    showingBandOnlyData: boolean = false;
    loading: boolean;
    noDataForPlotBands: boolean;
    plotBandsLoading: boolean;
    noDataForCompany: boolean;
    viewingBandNumber: number;
    noDataType: NoDataTypes;
    noDataTypePlotBands: NoDataTypes;
    format: 'number' | 'currency' | 'percentage' | any;
    bandsForLegend: KPIBand[];
    plotBandsList: Highcharts.AxisPlotBandsOptions[];
    kpiListSettings: any;
    kpiProfile: IPositionInfo[];
    selectedKPIProfile: IPositionInfo[];
    kpiProfileSettings: object;
    positionAbbv: string;
    parameters: IKPIDataParams;
    bottom10Top: number;

    constructor(
        private _KPIService: KPIService,
        private _router: Router,
        private reusableFunctions: ReusableFunctions,
        private currentUserStateService: CurrentUserStateService,
        private impersonationService: ImpersonationService,
        private decimalPipe: DecimalPipe,
        private toast: ToastService,
        private positionService: PositionService
    ) {
        this.kpiListSettings = {
            singleSelection: true,
            text: 'Select KPI',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'clientKPIMatrixDescription',
            primaryKey: 'kpiMatrixKey',
            enableFilterSelectAll: false,
        };

        this.kpiProfileSettings = {
            singleSelection: true,
            text: 'Select KPI profile',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'position',
            primaryKey: 'positionAbbv',
            enableFilterSelectAll: false,
        };
    }

    ngOnInit() {
        scroll(0, 0);
        this.selectedKPIProfile = [];
        this.plotBandsList = [];
        this.loading = true;
        this.plotBandsLoading = true;
        this.action = NoDataActionType.pageStart;
        this.noDataType = {
            action: this.action,
            searchTerm: '',
            componentTitle: this.pageTitle,
            inputSource: NoDataSourceType.chartCard,
        };

        this.noDataTypePlotBands = {
            action: this.action,
            searchTerm: '',
            componentTitle: 'Plot bands',
            inputSource: NoDataSourceType.chartCard,
        };

        let clientShortName = this.currentUserStateService.currentUser.clientShortName;
        if (clientShortName === 'LBA' && !this.impersonationService.isImpersonatingClient()) {
            return;
        }

        this.limeGearCharts = new LimeGearCharts.LimeGearChart(
            this._router,
            this.reusableFunctions,
            this.decimalPipe
        );
        this.hFilterJSON = this.reusableFunctions.getHierarchyFilters();
        this.displayOptions = this.reusableFunctions.getCurrentDisplayOptions(this._router.url);

        this.largeCardOptions = {
            action: this.action,
            componentTitle: this.pageTitle,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: false,
            accessToUrl: '',
            uniqueId: 'scatterplot',
            minHeight: 720,
            chart: null,
        };

        this.setDisplayOptionsSelected();
        this.showNames = true;
        this.setToggleLabel();
        this.setDDls();
    }

    setSelectedProfile() {
        let defaultProfileAbbv = 'LO';
        if (!(this.selectedKPIProfile && this.selectedKPIProfile.length > 0)) {
            let defaultProfile = this.kpiProfile.find(
                (pos) => pos.positionAbbv === defaultProfileAbbv
            );
            if (defaultProfile) {
                this.selectedKPIProfile = [defaultProfile];
            }
        }
        this.positionAbbv = this.selectedKPIProfile[0].positionAbbv;
    }

    setDDls() {
        this.loading = true;
        if (this.parameters?.roleName && this.parameters.roleName === 'Employee') {
            this.positionService.getScorecardPositions().subscribe(
                (res) => {
                    this.kpiProfile = res;
                    if (res && res.length > 0) {
                        this.setSelectedProfile();
                        this.getKpiList();
                    } else {
                        this.setCommonNoDataParameters();
                    }
                },
                (error) => {
                    this.action = NoDataActionType.error;
                    this.setCommonNoDataParameters();
                    throw new Error(error);
                }
            );
        } else {
            this.positionAbbv = 'LO';
            this.getKpiList();
        }
    }

    getKpiList() {
        this._KPIService
            .getKPIs({
                positionAbbv: this.positionAbbv,
            })
            .subscribe(
                (data) => {
                    if (data && data.length > 0) {
                        this.noDataForCompany = false;
                        this.KPIs = data;
                        let selectedKpi;
                        if (this.selectedKPI && this.selectedKPI.length > 0) {
                            selectedKpi = this.KPIs.find(
                                (kpi) => kpi.kpiMatrixKey === this.selectedKPI[0].kpiMatrixKey
                            );
                        }

                        if (selectedKpi) {
                            this.selectedKPI = [selectedKpi];
                        } else {
                            this.selectedKPI = [this.KPIs[0]];
                        }
                        this.parameters.positionAbbv = this.positionAbbv;
                        this.getKPIData();
                    } else {
                        this.setCommonNoDataParameters();
                    }
                },
                (err) => {
                    this.noDataType.action = NoDataActionType.error;
                    this.setCommonNoDataParameters();
                    throw new Error(err);
                }
            );
    }

    setDisplayOptionsSelected(): void {
        let fromDatePeriod = this.displayOptions.find(
            (item) => item.optionKey === 'dateRangeLastPeriod_y1_y_q_m'
        )?.selectedOptions;
        let loanType = this.displayOptions.find(
            (item) => item.optionKey === 'loanCategory'
        )?.selectedOptions;
        let loanPurpose = this.displayOptions.find(
            (item) => item.optionKey === 'loanPurpose'
        )?.selectedOptions;
        let channel = this.displayOptions.find(
            (item) => item.optionKey === 'channel'
        )?.selectedOptions;
        let roleName = this.displayOptions.find(
            (item) => item.optionKey === 'groupByRoles'
        )?.selectedOptions;

        this.parameters = {
            fromDatePeriod: fromDatePeriod ?? '',
            kpiMatrix: '',
            loanType: loanType ?? '',
            loanPurpose: loanPurpose ?? '',
            channel: channel ?? '',
            hFilterJSON: this.hFilterJSON,
            roleName: roleName ?? '',
            positionAbbv: '',
        };

        if (this.parameters.roleName !== 'Employee') {
            this.selectedKPIProfile = [];
        }
    }

    setChartData() {
        this.seriesData = [];
        this.records.forEach((record) => {
            if (this.parameters.roleName === 'Employee') {
                record.fullName = this.getFullName(record.hierarchyName);
                record.hierarchyName = this.abbreviateNameToLastPlustFirstInitial(
                    record.hierarchyName
                );
            } else {
                record.hierarchyName = record.hierarchyName.split(' - ').pop();
                record.fullName = record.hierarchyName;
            }

            let precision = record.kpiPrecision ?? 1;
            this.seriesData.push({
                name: record.hierarchyName,
                value: record.hierarchyIdentifier,
                properties: {
                    units: this.reusableFunctions.formatNumber(
                        record.kpiValue,
                        precision,
                        this.format,
                        false
                    ),
                    unitsLabel: this.selectedKPI[0].kpiDisplayShort,
                    titlePart: record.fullName,
                    nbr: record.nbr,
                },
                x: record.volume,
                y: this.formatPercentage(record.pctofTarget),
            });
        });
        this.setTopAndBottomPerformers();
        this.title = this.records[0].clientKPIMatrixDescription;
        this.drawChart();
    }

    formatPercentage(pctofTarget: number): number {
        if (pctofTarget > 99) {
            return Math.round(pctofTarget);
        }
        return this.reusableFunctions.round(pctofTarget, 1);
    }

    setChartDataForPerformanceBand(kpiBand: KPIBand) {
        let bandOnlyData = this.seriesData.filter((dataPoint) => {
            if (kpiBand.kpiBandKey === 1) {
                return dataPoint.y <= (kpiBand.pctEnd ? kpiBand.pctEnd : kpiBand.pctStart);
            } else if (kpiBand.kpiBandKey === 5) {
                return dataPoint.y >= (kpiBand.pctEnd ? kpiBand.pctEnd : kpiBand.pctStart);
            } else {
                return this.isDataPointWithBandRange(dataPoint.y, kpiBand.pctStart, kpiBand.pctEnd);
            }
        });
        if (bandOnlyData.length > 0) {
            this.viewingBandNumber = kpiBand.kpiBandKey;
            this.drawChart(bandOnlyData, true);
        } else {
            this.toast.warning('No producers in this performance band.');
        }
    }

    isDataPointWithBandRange(dataPoint: number, pctStart: number, pctEnd: number): boolean {
        let min = pctStart < pctEnd ? pctStart : pctEnd;
        let max = pctStart > pctEnd ? pctStart : pctEnd;
        return dataPoint >= min && dataPoint <= max;
    }

    drawChart(seriesData = this.seriesData, showingBandOnlyData = false) {
        if (seriesData) {
            this.setShowNames(seriesData.length);
        }
        this.showingBandOnlyData = showingBandOnlyData;
        setTimeout(() => {
            this.largeCardOptions.isDataReady = true;
            this.largeCardOptions.hasData = true;
            this.largeCardOptions.chart = new Highcharts.Chart(
                'scatterplot',
                this.limeGearCharts.createKPIScatterPlot(
                    this.title,
                    'Volume ($)',
                    '% of Target',
                    seriesData,
                    '#009999',
                    this.showNames,
                    this.parameters.roleName,
                    this.getUrlSegment()
                )
            );

            this.getBands();
            this.largeCardOptions = Object.assign({}, this.largeCardOptions);
        }, 0);
    }

    setShowNames(numberOfDataPoints: number): void {
        if (!this.shouldIgnoreNumberLOs) {
            numberOfDataPoints <= 25 ? (this.showNames = true) : (this.showNames = false);
        }
    }

    applyNewFilters(event: string) {
        this.hFilterJSON = event;
        this.action = NoDataActionType.filter;
        this.parameters.hFilterJSON = this.hFilterJSON;
        this.getKPIData();
    }

    getKPIData() {
        this.plotBandsLoading = true;
        this.viewingBandNumber = 0;
        this.loading = true;
        this.resetLargeCard();
        this.parameters.kpiMatrix = this.selectedKPI[0].kpiMatrixKey.toString();
        this.getData();
    }

    resetLargeCard() {
        this.largeCardOptions.isDataReady = false;
        this.largeCardOptions.hasData = false;
        this.largeCardOptions.chart = null;
        this.largeCardOptions = Object.assign({}, this.largeCardOptions);
    }

    setCommonNoDataParameters() {
        this.noDataForCompany = true;
        this.noDataForPlotBands = true;
        this.plotBandsLoading = false;
        this.loading = false;
        this.noDataTypePlotBands.action = this.action;
        this.noDataType.action = this.action;
    }

    getData() {
        this._KPIService.getData(this.parameters).subscribe(
            (data) => {
                this.loading = false;
                if (data && data.length > 0) {
                    this.noDataForCompany = false;
                    this.records = data;
                    this.format = data[0].kpiFormat;
                    this.setShowNames(this.records.length);
                    let targetPrecision = this.records[0].target > 99 ? 0 : 1;
                    this.targetUnits = this.reusableFunctions.formatNumber(
                        this.records[0].target,
                        targetPrecision,
                        this.records[0].kpiFormat,
                        true
                    );
                    this.setChartData();
                } else {
                    this.setCommonNoDataParameters();
                    this.largeCardOptions.isDataReady = true;
                    this.largeCardOptions.hasData = false;
                    this.largeCardOptions.chart = null;
                    this.largeCardOptions = Object.assign({}, this.largeCardOptions);
                }
            },
            (error) => {
                this.action = NoDataActionType.error;
                this.setCommonNoDataParameters();
                throw new Error(error);
            }
        );
    }

    getBands() {
        this.plotBandsLoading = true;
        this.noDataForPlotBands = false;
        this._KPIService
            .getBands({
                fromDatePeriod: this.parameters.fromDatePeriod,
                kpiMatrixKey: this.selectedKPI[0].kpiMatrixKey,
            })
            .subscribe(
                (bands) => {
                    this.plotBandsLoading = false;
                    if (bands && bands.length > 0) {
                        this.plotBandsHighChart = bands;
                        this.setPlotBands();
                    } else {
                        this.plotBandsHighChart = [];
                        this.noDataForPlotBands = true;
                    }
                },
                (error) => {
                    this.noDataForPlotBands = true;
                    this.plotBandsLoading = false;
                    this.noDataTypePlotBands.action = NoDataActionType.error;
                    throw new Error(error);
                }
            );
    }

    handleNoData(message: string) {
        this.toast.info(message);
        this.topEntities = this.bottomEntities = [];
        this.records = undefined;
        this.format = null;
    }

    setPlotBands(): void {
        this.setBandNames();
        this.setBandDescriptions();
        this.bandsForLegend = this.plotBandsHighChart.reverse();
        this.setBandClassNames();
        this.setPlotBandsList();
        this.applyPlotBands();
    }

    applyPlotBands() {
        this.plotBandsList.forEach((band) => {
            this.largeCardOptions.chart.yAxis[0].addPlotBand(band);
        });
    }

    setPlotBandsList(isHigherBetter: boolean = true) {
        let plotBands = this.plotBandsHighChart;
        // TODO: simplify the logic below
        let usePlotBands = plotBands?.length === 5 ? true : false;
        this.plotBandsList = [
            {
                color: '#FDDBDB',
                from: usePlotBands
                    ? isHigherBetter
                        ? -Math.pow(2, 31)
                        : plotBands.find((band) => band.kpiBandKey === 1).pctStart
                        ? plotBands.find((band) => band.kpiBandKey === 1).pctStart
                        : plotBands.find((band) => band.kpiBandKey === 1).pctEnd
                    : null,
                to: usePlotBands
                    ? isHigherBetter
                        ? plotBands.find((band) => band.kpiBandKey === 1).pctEnd
                            ? plotBands.find((band) => band.kpiBandKey === 1).pctEnd
                            : plotBands.find((band) => band.kpiBandKey === 1).pctStart
                        : Math.pow(2, 31)
                    : null,
            },
            {
                color: '#FFF9E7',
                from: usePlotBands
                    ? plotBands.find((band) => band.kpiBandKey === 2).pctStart
                    : null,
                to: usePlotBands ? plotBands.find((band) => band.kpiBandKey === 2).pctEnd : null,
            },
            {
                color: '#E0F3F3',
                from: usePlotBands
                    ? plotBands.find((band) => band.kpiBandKey === 3).pctStart
                    : null,
                to: usePlotBands ? plotBands.find((band) => band.kpiBandKey === 3).pctEnd : null,
            },
            {
                color: '#F7EFFF',
                from: usePlotBands
                    ? plotBands.find((band) => band.kpiBandKey === 4).pctStart
                    : null,
                to: usePlotBands ? plotBands.find((band) => band.kpiBandKey === 4).pctEnd : null,
            },
            {
                color: '#F3F9ED',
                from: usePlotBands
                    ? isHigherBetter
                        ? plotBands.find((band) => band.kpiBandKey === 5).pctStart
                            ? plotBands.find((band) => band.kpiBandKey === 5).pctStart
                            : plotBands.find((band) => band.kpiBandKey === 5).pctEnd
                        : -Math.pow(2, 31)
                    : null,
                to: usePlotBands
                    ? isHigherBetter
                        ? Math.pow(2, 31)
                        : plotBands.find((band) => band.kpiBandKey === 5).pctEnd
                        ? plotBands.find((band) => band.kpiBandKey === 5).pctEnd
                        : plotBands.find((band) => band.kpiBandKey === 5).pctStart
                    : null,
            },
        ];
    }

    setBandNames(): void {
        for (let band of this.plotBandsHighChart) {
            band.name = this.getBandName(band.kpiBandKey);
        }
    }

    setBandClassNames(): void {
        for (let band of this.plotBandsHighChart) {
            band.className = this.getClassName(band.kpiBandKey);
        }
    }

    getBandName(bandKey: number): any {
        switch (bandKey) {
            case 1:
                return 'Poor';
            case 2:
                return 'Fair';
            case 3:
                return 'Good';
            case 4:
                return 'Very Good';
            case 5:
                return 'Excellent';
        }
    }

    getClassName(bandKey: number): any {
        switch (bandKey) {
            case 1:
                return 'unacceptable-band';
            case 2:
                return 'below-standards-band';
            case 3:
                return 'acceptable-band';
            case 4:
                return 'above-standards-band';
            case 5:
                return 'exceptional-band';
        }
    }

    determineIfHigherIsBetter(): boolean {
        let band2Value = this.plotBandsHighChart[1].rangeStart
            ? this.plotBandsHighChart[1].rangeStart
            : this.plotBandsHighChart[1].rangeEnd;
        let band4Value = this.plotBandsHighChart[3].rangeStart
            ? this.plotBandsHighChart[3].rangeStart
            : this.plotBandsHighChart[3].rangeEnd;
        return band2Value < band4Value;
    }

    setTopAndBottomPerformers(): void {
        let totalLength = this.records.length;
        let topChunkSize = 10;
        let bottomChunkSize = 10;

        if (totalLength <= 20) {
            topChunkSize = Math.round(totalLength / 2);
            bottomChunkSize = totalLength - topChunkSize;
        }

        this.bottom10Top = totalLength - bottomChunkSize;
        let sortedData = this.records.sort(comparePercentOfTarget);
        this.topEntities = sortedData.slice(0, topChunkSize);
        this.bottomEntities = sortedData.slice(this.bottom10Top, totalLength);
        this.formatTopBottom10(this.topEntities);
        this.formatTopBottom10(this.bottomEntities);
    }

    formatTopBottom10(data: KPIData[]) {
        data.forEach((entity) => {
            entity.kpiValueFormatted = this.reusableFunctions.formatNumber(
                entity.kpiValue,
                entity.kpiPrecision ?? 0,
                entity.kpiFormat,
                true
            );
        });
    }

    formatKPIDesc(kpiValue: number, decimalPlaces: number): string {
        return this.reusableFunctions.formatNumber(kpiValue, decimalPlaces, this.format, true);
    }

    abbreviateNameToLastPlustFirstInitial(fullName: string): string {
        let nameParts = fullName.split(', ');
        if (nameParts.length < 2) {
            return fullName;
        }
        return nameParts[0] + ', ' + nameParts[1].charAt(0);
    }

    formatRangePointDesc(bandKey: number): string {
        let kpiBand: KPIBand = this.plotBandsHighChart.find((band) => band.kpiBandKey === bandKey);
        let rangeStart = this.formatKPIDesc(kpiBand.rangeStart, kpiBand.places);
        let rangeEnd = this.formatKPIDesc(kpiBand.rangeEnd, kpiBand.places);
        let description = `${rangeStart} - ${rangeEnd}`;
        return description;
    }

    formatRangeEndDesc(bandKey: number): string {
        let isHigherBetter = this.determineIfHigherIsBetter();
        let kpiBand: KPIBand = this.plotBandsHighChart.find((band) => band.kpiBandKey === bandKey);

        let rangeStart = this.formatKPIDesc(kpiBand.rangeStart, kpiBand.places);
        let rangeEnd = this.formatKPIDesc(kpiBand.rangeEnd, kpiBand.places);
        let value = kpiBand.rangeStart ? rangeStart : rangeEnd;

        let description: string;
        if (bandKey == 5) {
            description = isHigherBetter ? `&ge; ${value}` : `&le; ${value}`;
        } else {
            description = isHigherBetter ? `&le; ${value}` : `&ge; ${value}`;
        }
        return description;
    }

    setBandDescriptions(): void {
        this.plotBandsHighChart[4].description = this.formatRangeEndDesc(5);
        this.plotBandsHighChart[3].description = this.formatRangePointDesc(4);
        this.plotBandsHighChart[2].description = this.formatRangePointDesc(3);
        this.plotBandsHighChart[1].description = this.formatRangePointDesc(2);
        this.plotBandsHighChart[0].description = this.formatRangeEndDesc(1);
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.action = NoDataActionType.filter;
        this.displayOptions = event;
        this.setDisplayOptionsSelected();
        this.setDDls();
    }

    setToggleLabel() {
        if (this.showNames) {
            this.switchLabel = 'Show Names';
        } else {
            this.switchLabel = 'Hide Names';
        }
    }

    toggleDataLabels(event): void {
        // TODO: stop resetting chart
        this.shouldIgnoreNumberLOs = true;
        this.setToggleLabel();
        this.showNames = event.checked;
        this.drawChart();
    }

    zoomInOnBand(band: KPIBand) {
        if (band.kpiBandKey !== this.viewingBandNumber) {
            this.setChartDataForPerformanceBand(band);
        }
    }

    mouseEnter(employee: KPIData) {
        let scatterPlot = this.largeCardOptions.chart;
        let employeeName = employee.hierarchyName;
        let seriesData = scatterPlot?.series[0]?.data;
        if (this.parameters.roleName != 'Employee') {
            employeeName = employee.hierarchyName;
        }
        seriesData?.forEach((point) => {
            if (point.name === employeeName) {
                point.setState('hover');
                scatterPlot.tooltip.refresh(point);
            }
        });
    }

    getUrlSegment() {
        return this.selectedKPIProfile && this.selectedKPIProfile[0]
            ? this.selectedKPIProfile[0].scoreCardUrlSegment
            : 'branch';
    }

    mouseLeave(employee: KPIData) {
        let scatterPlot = this.largeCardOptions.chart;
        let employeeName = employee.hierarchyName;
        let seriesData = scatterPlot.series[0].data;
        if (this.parameters.roleName != 'Employee') {
            employeeName = employee.hierarchyName;
        }
        seriesData.forEach((point) => {
            if (point.name === employeeName) {
                point.setState('normal');
            }
        });
        scatterPlot.tooltip.hide(100);
    }

    onSelectionChangeKpi(kpiDescription) {
        this.action = NoDataActionType.filter;
        this.getKPIData();
    }

    onSelectionChangeProfile(event) {
        this.action = NoDataActionType.filter;
        this.setSelectedProfile();
        this.getKpiList();
    }

    topBottomNameClickHandler(record: KPIData) {
        let urlSegment = this.getUrlSegment();
        let queryParams: object = { id: record.hierarchyIdentifier };
        if (this.parameters.roleName !== 'Employee') {
            queryParams['level'] = this.parameters.roleName;
        }

        this._router.navigate(['/performance/scorecards/' + urlSegment], {
            queryParams: queryParams,
        });
    }

    onDeSelect(event) {
        this.selectedKPI = [event];
    }

    onDeSelectProfile(event) {
        this.selectedKPIProfile = [event];
    }

    getFullName(hierarchyName) {
        let nameParts = hierarchyName.split(', ');
        let firstName = nameParts.length > 1 ? nameParts[1] : '';
        let lastName = nameParts[0].length > 1 ? nameParts[0] : '';
        return firstName + ' ' + lastName;
    }
}

function comparePercentOfTarget(a, b) {
    if (a.pctofTarget > b.pctofTarget) {
        return -1;
    }
    if (a.pctofTarget < b.pctofTarget) {
        return 1;
    } else if (a.volume > b.volume) {
        return -1;
    } else if (a.volume < b.volume) {
        return 1;
    }
    return 0;
}
